import React, { useState, useEffect } from "react";
import { Switch, Route } from "react-router-dom";
import { Panel, Home } from "./components";
import { Register } from "./components/Auth/Register";
import { Login, ForgotPassword } from "./components/Auth/Login";
import i18n from "./i18n/locales/config";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import {
  LeftSectionContextProvider,
  ScenarioContextProvider,
  AutomationContextProvider,
  ProfileContextProvider,
  LumpContextProvider,
  DeviceContextProvider,
  CameraContextProvider,
} from "./context";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { store } from "./redux/store";
import { Provider } from "react-redux";
import { DeviceMineProvider } from "./context/DeviceMineContext";
import "./styles/ModalsDesign.css";

const App = () => {
  const { i18n: i18nInstance } = useTranslation();
  const [theme, setTheme] = useState("light");
  const [openModal, setopenModal] = useState(false);
  
  const toggle = () => {
    setopenModal(!openModal);
  };

  // Initialize theme and language from localStorage on app load
  useEffect(() => {
    // Theme initialization
    const savedTheme = localStorage.getItem("theme") || "light";
    setTheme(savedTheme);
    document.documentElement.setAttribute("data-theme", savedTheme);
    
    // Language initialization - CRITICAL FIX
    const savedLanguage = localStorage.getItem("language") || Cookies.get("language") || "en";
    // Update i18n instance
    i18nInstance.changeLanguage(savedLanguage);
    // Update localStorage to ensure consistency
    localStorage.setItem("language", savedLanguage);
    // Update body direction
    if (savedLanguage === "fa") {
      document.querySelector("body")?.classList.add("dirRTL");
      document.querySelector("body")?.classList.remove("dirLTR");
      document.documentElement.setAttribute("dir", "rtl");
    } else {
      document.querySelector("body")?.classList.add("dirLTR");
      document.querySelector("body")?.classList.remove("dirRTL");
      document.documentElement.setAttribute("dir", "ltr");
    }
    document.documentElement.setAttribute("lang", savedLanguage);
    
    // Update cookie for consistency
    Cookies.set("language", savedLanguage);
  }, [i18nInstance]);

  // Listen for storage events to sync across tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'theme' && e.newValue) {
        setTheme(e.newValue);
        document.documentElement.setAttribute("data-theme", e.newValue);
      }
      if (e.key === 'language' && e.newValue) {
        i18nInstance.changeLanguage(e.newValue);
        document.documentElement.setAttribute("dir", e.newValue === "fa" ? "rtl" : "ltr");
        document.documentElement.setAttribute("lang", e.newValue);
        if (e.newValue === "fa") {
          document.querySelector("body")?.classList.add("dirRTL");
          document.querySelector("body")?.classList.remove("dirLTR");
        } else {
          document.querySelector("body")?.classList.add("dirLTR");
          document.querySelector("body")?.classList.remove("dirRTL");
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [i18nInstance]);

  // Listen for custom events from ThemeLanguageToggle
  useEffect(() => {
    const handleThemeChange = (e) => {
      setTheme(e.detail.theme);
    };
    
    const handleLanguageChange = (e) => {
      i18nInstance.changeLanguage(e.detail.language);
      document.documentElement.setAttribute("dir", e.detail.language === "fa" ? "rtl" : "ltr");
      document.documentElement.setAttribute("lang", e.detail.language);
      if (e.detail.language === "fa") {
        document.querySelector("body")?.classList.add("dirRTL");
        document.querySelector("body")?.classList.remove("dirLTR");
      } else {
        document.querySelector("body")?.classList.add("dirLTR");
        document.querySelector("body")?.classList.remove("dirRTL");
      }
    };
    
    window.addEventListener('themeChange', handleThemeChange);
    window.addEventListener('languageChange', handleLanguageChange);
    
    return () => {
      window.removeEventListener('themeChange', handleThemeChange);
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, [i18nInstance]);

  useEffect(() => {
    toggle();
  }, []);

  const clickH = () => {
    document.documentElement.requestFullscreen();
    toggle();
  };

  return (
    <>
      <Provider store={store}>
        <div className={`app-wrapper ${theme}`} data-theme={theme}>
          <div
            className="d-flex flex-wrap"
            style={{ 
              minHeight: "100vh", 
              margin: "0px", 
              background: "var(--bg-light, #f7fafc)",
              color: "var(--text-primary, #1a202c)"
            }}
          >
            <Switch>
              <Route exact path="/" component={Home} />
              <Route exact path="/login" component={Login} />
              <Route exact path="/register" component={Register} />
              <Route exact path="/forgotpassword" component={ForgotPassword} />
              <LeftSectionContextProvider>
                <ScenarioContextProvider>
                  <AutomationContextProvider>
                    <ProfileContextProvider>
                      <LumpContextProvider>
                        <DeviceMineProvider>
                          <DeviceContextProvider>
                            <CameraContextProvider>
                              <Route exact path="/panel" component={Panel} />
                            </CameraContextProvider>
                          </DeviceContextProvider>
                        </DeviceMineProvider>
                      </LumpContextProvider>
                    </ProfileContextProvider>
                  </AutomationContextProvider>
                </ScenarioContextProvider>
              </LeftSectionContextProvider>
            </Switch>
          </div>
        </div>
      </Provider>
    </>
  );
};

export default App;